import React, { ReactElement, useEffect, useRef, useState, ReactNode } from 'react'
import { Flex, Text } from 'theme-ui'
import { Dropdown, Svg } from 'ui'
import type { Setter } from 'utils'
import DropdownArrowIcon from 'images/homePage/dropdown-arrow.svg'

interface FeaturesDropdownProps {
  dropdownData: ReactNode[]
  selectedItemIndex: number
  setSelectedItemIndex: Setter<number>
}

const FeaturesDropdown = ({
  dropdownData,
  selectedItemIndex,
  setSelectedItemIndex,
}: FeaturesDropdownProps): ReactElement => {
  const hideRef = useRef<() => void>()
  const dropdownContainerRef = useRef<HTMLDivElement>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  useEffect(() => {
    !isDropdownOpen && hideRef?.current?.()
  }, [isDropdownOpen])

  return (
    <Dropdown
      ariaLabel="features select dropdown"
      data-testid="features-select-dropdown"
      variant="simple"
      sx={{
        px: '1.6rem',
        py: '0.8rem',
        maxWidth: '100%',
        backgroundColor: 'transparent',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid',
        borderColor: 'white15',
        borderRadius: '8px',
      }}
      tippyprops={{
        offset: [-50, 0],
        placement: 'bottom',
        animation: 'fade',
        onCreate: instance => {
          hideRef.current = () => {
            instance.hide()
          }
        },
        onHide() {
          setIsDropdownOpen(false)
        },
        onTrigger() {
          setIsDropdownOpen(true)
        },
        maxWidth: 'fit-content',
        theme: 'transparent',
      }}
      dropdowncontent={
        <Flex
          sx={{
            flexDirection: 'column',
            // 32px - padding on the right and left sides by the parent element 16px.
            // this must be considered in order to set the correct width
            width: `calc(${dropdownContainerRef.current?.clientWidth}px + 32px)`,
            boxShadow: '0 10px 15px 0 rgba(0, 0, 0, 0.25)',
            border: 'solid 1px',
            borderColor: 'darkItemBG50',
            backgroundColor: 'white',
            borderRadius: '8px',
            p: '0.8rem',
          }}
        >
          {dropdownData?.map((item, index) => (
            <Text
              key={index}
              sx={{
                color: 'black',
                backgroundColor: selectedItemIndex === index && 'black10',
                fontWeight: selectedItemIndex === index && 'bold',
                p: '0.8rem',
                borderRadius: '6px',
              }}
              onClick={() => {
                setSelectedItemIndex(index)
                setIsDropdownOpen(false)
              }}
            >
              {item}
            </Text>
          ))}
        </Flex>
      }
    >
      <Flex
        ref={dropdownContainerRef}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text
          sx={{
            fontSize: '1.4rem',
            fontWeight: 'bold',
            color: 'white',
          }}
        >
          {dropdownData[selectedItemIndex]}
        </Text>
        <Svg
          svg={DropdownArrowIcon}
          fill="aliceBlue60"
          descriptiveText={
            isDropdownOpen ? 'Collapse features dropdown' : 'Expand features dropdown'
          }
        />
      </Flex>
    </Dropdown>
  )
}

export default FeaturesDropdown
